import InformationIcon from '@/components/icons/information';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TaskProgressProps {
  completed: number;
  total: number;
  openIssueCount?: number;
  openCheckCount?: number;
  className?: string;
}

export function TaskProgress({ completed, total, openIssueCount, openCheckCount, className }: TaskProgressProps) {
  if (total === 0) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div className={cn('flex h-5 w-full items-center', className)}>
              <div className="bg-grey-3 h-3 flex-1 rounded-l-full" />
              <InformationIcon className="text-grey-5 mx-1 shrink-0" />
              <div className="bg-grey-3 h-3 flex-1 rounded-r-full" />
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
            Select issues or schedule checks to track progress
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const isComplete = completed === total;
  const fillColor = isComplete ? 'rgb(var(--default-green))' : 'rgb(var(--default-yellow))';
  const stripeSecondary = isComplete ? 'rgb(var(--secondary-green))' : 'rgb(var(--secondary-yellow))';
  const stripeBackground = `repeating-linear-gradient(45deg, ${fillColor}, ${fillColor} 8px, ${stripeSecondary} 8px, ${stripeSecondary} 16px)`;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className={cn('flex h-5 w-full items-center gap-1', className)} aria-label={`${completed} of ${total} tasks complete`}>
            {Array.from({ length: total }, (_, i) => {
              const filled = i < completed;
              return (
                <div
                  key={i}
                  className={cn('h-3 flex-1 rounded-full transition-colors duration-300', !filled && 'bg-grey-3')}
                  style={filled ? { background: stripeBackground } : undefined}
                />
              );
            })}
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
          <ul className="flex flex-col gap-1">
            {(openIssueCount === undefined || openIssueCount === 0) && (openCheckCount === undefined || openCheckCount === 0) && (
              <li className="flex items-center gap-2">
                <span>•</span>
                <span>All tasks complete</span>
              </li>
            )}
            {openIssueCount != null && openIssueCount > 0 && (
              <li className="flex items-center gap-2">
                <span>•</span>
                <span>
                  {openIssueCount} {openIssueCount === 1 ? 'issue' : 'issues'} remaining
                </span>
              </li>
            )}
            {openCheckCount != null && openCheckCount > 0 && (
              <li className="flex items-center gap-2">
                <span>•</span>
                <span>
                  {openCheckCount} {openCheckCount === 1 ? 'schedule check' : 'schedule checks'} remaining
                </span>
              </li>
            )}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
